import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useParams } from 'react-router-dom';
import { useCartContext } from '../../../context/CartContext';
import { useRestaurantContext } from '../../../context/RestaurantContext';
import { joinQueue } from '../../../services/api/restApiAdapter';
import { useAuth } from '../../../context/AuthContext';
import MoneyUtils from '../../../utils/moneyUtils';

const JoinQueueSheet = ({
  isOpen,
  onClose,
  theme,
  onJoinSuccess
}) => {
  const { 
    cartData, 
    clearCart, 
    isQueueMode, 
    setIsQueueMode, 
    queueGuestInfo, 
    setQueueGuestInfo 
  } = useCartContext();
  const { details, restaurantId: contextRestaurantId, selfOrderConfig } = useRestaurantContext();
  const { restaurantId: paramRestaurantId } = useParams();
  const restaurantId = paramRestaurantId || contextRestaurantId;
  const navigate = useNavigate();
  const { currentUser, ensureGuestAuth } = useAuth();
  
  // Initialize from context or defaults
  const [partySize, setPartySize] = useState(2);
  const [guestName, setGuestName] = useState('');
  const [guestMobile, setGuestMobile] = useState('');
  const [notes, setNotes] = useState('');
  const [wantPreOrder, setWantPreOrder] = useState(false);
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showExactCount, setShowExactCount] = useState(false);
  const [error, setError] = useState(null);

  // Theme derivation matching CartBottomSheet:
  // primary is text on brand color, secondary is the brand color itself.
  const textPrimary = theme?.primary || '#fff';
  const brandSecondary = theme?.secondary || '#000';

  const hasItems = cartData?.items?.length > 0;

  // Update local state when context changes (e.g. returning from menu)
  useEffect(() => {
    if (isOpen) {
      if (queueGuestInfo) {
        setPartySize(queueGuestInfo.partySize || 2);
        setGuestName(queueGuestInfo.name || '');
        setGuestMobile(queueGuestInfo.phone || '');
        setNotes(queueGuestInfo.notes || '');
      }
      
      // If they have items, they are already in pre-order flow
      if (cartData?.items?.length > 0) {
        setWantPreOrder(true);
      }
    }
  }, [isOpen, queueGuestInfo, cartData?.items?.length]);

  const handlePreOrderFlow = () => {
    // Save current details to context
    setQueueGuestInfo({
      name: guestName,
      phone: guestMobile,
      partySize,
      notes
    });
    setIsQueueMode(true);
    onClose(); // Close sheet
    navigate(`/restaurant/${restaurantId}`); // Go to menu
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    setError(null);
    if (!guestName || !guestMobile) {
      setError('Please enter your name and mobile number');
      return;
    }

    setIsSubmitting(true);
    try {
      // Ensure user is authenticated (anonymous guest)
      const user = await ensureGuestAuth();

      const payload = {
        name: guestName,
        phone: guestMobile,
        partySize,
        notes,
        userId: user?.uid || null,
        // Send items if in queue mode or if items exist
        items: (isQueueMode || hasItems) ? cartData.items.map(item => ({
          menuItemId: item.id || item._id,
          name: item.name,
          price: item.price,
          quantity: item.quantity,
          customizations: item.customizations || null,
          cookingInstruction: item.specialInstructions || null,
          isCombo: item.isCombo || false,
          comboComponents: item.customizations?.comboComponents || null,
        })) : null
      };

      const response = await joinQueue(restaurantId, payload);
      if (response.success) {
        // Clear guest info and cart on success
        setQueueGuestInfo(null);
        if (isQueueMode || hasItems) clearCart();
        onJoinSuccess(response.data);
        onClose();
      } else {
        setError(response.error || 'Failed to join queue');
      }
    } catch (error) {
      console.error('Error joining queue:', error);
      setError('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[80]"
          />

          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed bottom-0 left-0 right-0 bg-gray-50 rounded-t-3xl z-[90] max-h-[85vh] flex flex-col shadow-2xl overflow-hidden"
          >
            {/* Header */}
            <div className="bg-white rounded-t-3xl border-b border-gray-100 flex items-center justify-between px-5 pt-4 pb-3 shrink-0">
              <h2 className="text-xl font-bold text-gray-900">Join Queue</h2>
              <button
                onClick={onClose}
                className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-4 pb-32">
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 space-y-6">
                {/* Party Size Selector */}
                <div className="space-y-2.5">
                  <label className="text-[10px] font-black text-gray-500 uppercase tracking-[0.2em] px-1">How many guests?</label>
                  <div className="flex flex-col gap-2.5">
                    <div className="flex items-center justify-between bg-gray-50/50 p-1 rounded-2xl border border-gray-100/50">
                      {[1, 2, 3, 4, 5].map(num => (
                        <button
                          key={num}
                          onClick={() => {
                            setPartySize(num);
                            setShowExactCount(false);
                          }}
                          className={`w-10 h-10 rounded-xl text-base font-black transition-all duration-300 ${
                            partySize === num && !showExactCount
                              ? 'shadow-[0_8px_20px_-6px_rgba(0,0,0,0.2)] scale-105' 
                              : 'text-gray-600 hover:text-gray-900'
                          }`}
                          style={partySize === num && !showExactCount ? { color: textPrimary, backgroundColor: brandSecondary } : {}}
                        >
                          {num}
                        </button>
                      ))}
                      <button
                        onClick={() => setShowExactCount(true)}
                        className={`w-10 h-10 rounded-xl text-[10px] font-black transition-all duration-300 ${
                          showExactCount 
                            ? 'shadow-[0_8px_20px_-6px_rgba(0,0,0,0.2)] scale-105' 
                            : 'text-gray-600 hover:text-gray-900'
                        }`}
                        style={showExactCount ? { color: textPrimary, backgroundColor: brandSecondary } : {}}
                      >
                        MORE
                      </button>
                    </div>

                    {showExactCount && (
                      <motion.div 
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="bg-gray-50/50 p-3 rounded-2xl border border-gray-100/50 flex items-center gap-3"
                      >
                        <span className="text-[9px] font-black text-gray-500 uppercase tracking-widest pl-1">Exact:</span>
                        <input
                          type="number"
                          min="6"
                          placeholder="6"
                          value={partySize}
                          onChange={(e) => setPartySize(parseInt(e.target.value) || 1)}
                          className="flex-1 bg-white h-9 px-3 rounded-xl border border-gray-100 font-bold text-base focus:ring-2 focus:ring-black/5 focus:border-gray-300 outline-none transition-all"
                        />
                      </motion.div>
                    )}
                  </div>
                </div>

                {/* Form Inputs */}
                <div className="space-y-4">
                  <div className="space-y-1">
                    <label className="text-[10px] font-black text-gray-500 uppercase tracking-[0.2em] px-1">Full Name</label>
                    <input
                      type="text"
                      value={guestName}
                      onChange={(e) => setGuestName(e.target.value)}
                      placeholder="Enter your name"
                      className="w-full h-12 px-4 bg-white border border-gray-200 rounded-2xl text-sm font-bold placeholder:text-gray-300 focus:ring-2 focus:ring-black/5 focus:border-gray-300 outline-none transition-all shadow-sm"
                      required
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="text-[10px] font-black text-gray-500 uppercase tracking-[0.2em] px-1">Mobile Number</label>
                    <div className="relative">
                      <input
                        type="tel"
                        value={guestMobile}
                        onChange={(e) => setGuestMobile(e.target.value.replace(/\D/g, ''))}
                        placeholder="00000 00000"
                        className="w-full h-12 px-4 bg-white border border-gray-200 rounded-2xl text-sm font-bold font-mono tracking-[0.2em] placeholder:text-gray-300 focus:ring-2 focus:ring-black/5 focus:border-gray-300 outline-none transition-all shadow-sm"
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-1">
                    <label className="text-[10px] font-black text-gray-500 uppercase tracking-[0.2em] px-1">Notes (Optional)</label>
                    <textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Special requests, high chair, etc."
                      className="w-full px-4 py-3 bg-white border border-gray-200 rounded-2xl text-sm font-bold placeholder:text-gray-300 focus:ring-2 focus:ring-black/5 focus:border-gray-300 outline-none transition-all resize-none h-20 shadow-sm"
                    />
                  </div>

                  {/* Pre-order Toggle */}
                  {!isQueueMode && cartData?.items?.length === 0 && selfOrderConfig?.queueManagement?.allowPreOrder && (
                    <div 
                      onClick={() => setWantPreOrder(!wantPreOrder)}
                      className={`p-4 rounded-[28px] border-2 transition-all duration-300 cursor-pointer flex items-center gap-4 ${
                        wantPreOrder 
                          ? 'border-brandSecondary/20 bg-brandSecondary/[0.03] shadow-[0_10px_30px_-10px_rgba(0,0,0,0.1)]' 
                          : 'border-gray-50 bg-white hover:border-gray-100'
                      }`}
                      style={wantPreOrder ? { borderColor: `${brandSecondary}33` } : {}}
                    >
                      <div className={`w-11 h-11 rounded-2xl flex items-center justify-center text-xl transition-all duration-300 ${
                        wantPreOrder ? 'scale-110 shadow-lg' : 'bg-gray-50 text-gray-400'
                      }`}
                      style={wantPreOrder ? { backgroundColor: brandSecondary, color: textPrimary, boxShadow: `0 8px 20px -4px ${brandSecondary}40` } : {}}
                      >
                        🥡
                      </div>
                      <div className="flex-1">
                        <p className="text-xs font-black text-gray-900 tracking-tight">Want to pre-order?</p>
                        <p className="text-[9px] font-bold text-gray-400 uppercase tracking-[0.1em] mt-0.5">Save time & see the menu</p>
                      </div>
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all duration-300 ${
                        wantPreOrder ? 'rotate-0' : 'border-gray-200 bg-white rotate-180'
                      }`}
                      style={wantPreOrder ? { backgroundColor: brandSecondary, borderColor: brandSecondary } : {}}
                      >
                        {wantPreOrder && (
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: textPrimary }}>
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={4} d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Pre-Order Preview */}
                {cartData?.items?.length > 0 && selfOrderConfig?.queueManagement?.allowPreOrder && (
                  <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ backgroundColor: brandSecondary }} />
                        <h3 className="text-[10px] font-black uppercase tracking-[0.2em]" style={{ color: brandSecondary }}>Pre-Order Summary</h3>
                      </div>
                      <span className="text-[9px] font-black px-2 py-1 rounded-lg shadow-sm" style={{ backgroundColor: brandSecondary, color: textPrimary }}>
                        {cartData.items.length} ITEMS
                      </span>
                    </div>
                    <div className="space-y-2.5 max-h-32 overflow-y-auto pr-1">
                      {cartData.items.map((item, idx) => (
                        <div key={idx} className="flex justify-between text-xs font-bold text-gray-700">
                          <span className="flex items-center gap-2">
                            <span className="text-[10px] opacity-40" style={{ color: brandSecondary }}>{item.quantity}x</span>
                            {item.name}
                          </span>
                          <span className="text-gray-400">{MoneyUtils.format(item.price * item.quantity)}</span>
                        </div>
                      ))}
                    </div>
                    <div className="pt-4 border-t border-gray-100 space-y-1.5">
                      <div className="flex justify-between items-center text-[9px] font-black text-gray-400 uppercase tracking-widest">
                        <span>Subtotal</span>
                        <span>{MoneyUtils.format(cartData.subtotal)}</span>
                      </div>
                      {cartData.tax > 0 && (
                        <div className="flex justify-between items-center text-[9px] font-black text-gray-400 uppercase tracking-widest">
                          <span>Taxes</span>
                          <span>{MoneyUtils.format(cartData.tax)}</span>
                        </div>
                      )}
                      <div className="pt-2 flex justify-between items-end">
                        <span className="text-[10px] font-black text-gray-900 uppercase tracking-[0.15em]">Total Amount</span>
                        <span className="text-xl font-black leading-none tracking-tight" style={{ color: brandSecondary }}>{MoneyUtils.format(cartData.total)}</span>
                      </div>
                    </div>
                    <button 
                      onClick={() => {
                        setQueueGuestInfo({ name: guestName, phone: guestMobile, partySize, notes });
                        onClose();
                      }}
                      className="w-full py-3 text-[10px] font-black uppercase tracking-[0.25em] bg-white rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition-all active:scale-[0.98]"
                      style={{ color: brandSecondary }}
                    >
                      Modify Items
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Submit Section */}
            <div className="absolute bottom-0 left-0 right-0 bg-white/95 backdrop-blur-xl border-t border-gray-100 z-40 pb-safe px-4 pt-4 shadow-[0_-10px_40px_rgba(0,0,0,0.06)]"
                 style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 16px)' }}>
              <div className="flex items-center justify-end gap-2">
                <button
                  onClick={wantPreOrder && cartData?.items?.length === 0 ? handlePreOrderFlow : handleSubmit}
                  disabled={isSubmitting || !guestName || guestMobile.length < 10}
                  className="w-full py-3 px-2 rounded-xl text-[11px] font-black shadow-[0_8px_20px_rgba(0,0,0,0.15)] disabled:opacity-50 transition-all flex items-center justify-center uppercase tracking-wider scale-wrapper active:scale-95"
                  style={{ 
                    backgroundColor: brandSecondary, 
                    color: textPrimary,
                  }}
                >
                  {isSubmitting ? (
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" style={{ borderTopColor: textPrimary }} />
                  ) : (
                    <>
                      {wantPreOrder && cartData?.items?.length === 0 
                        ? 'See Menu' 
                        : 'Confirm Join'}
                    </>
                  )}
                </button>
              </div>
            </div>
          </motion.div>

          {/* Error Modal (Dialog) */}
          <AnimatePresence>
            {error && (
              <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                <motion.div 
                  key="error-backdrop"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                  onClick={() => setError(null)}
                />
                <motion.div 
                  key="error-dialog"
                  initial={{ opacity: 0, scale: 0.9, y: 20 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.9, y: 20 }}
                  className="relative w-full max-w-sm bg-white rounded-[32px] p-6 shadow-2xl flex flex-col max-h-[85vh] z-[101]"
                >
                  <div className="flex flex-col items-center text-center overflow-y-auto mb-6 px-2">
                    <div className="w-16 h-16 bg-red-50 text-red-500 rounded-2xl flex items-center justify-center text-3xl mb-5 shadow-sm shrink-0">
                      ⚠️
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3 shrink-0">Waitlist Issue</h3>
                    <p className="text-sm text-gray-500 leading-relaxed">
                      {error}
                    </p>
                  </div>
                  <button
                    onClick={() => setError(null)}
                    className="w-full py-4 rounded-2xl font-black text-sm uppercase tracking-widest transition-all active:scale-95 shrink-0"
                    style={{ backgroundColor: brandSecondary, color: textPrimary }}
                  >
                    Okay, I Understand
                  </button>
                </motion.div>
              </div>
            )}
          </AnimatePresence>
        </>
      )}
    </AnimatePresence>
  );
};

export default JoinQueueSheet;
